import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, MapPin, Phone, Mail, Globe, Clock, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SITE } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=2000&q=80";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["Home", "Apartment", "Office", "Rental Property", "Airbnb or VRBO", "Other"];
const SERVICES = ["Commercial Cleaning", "Residential Cleaning", "Deep Cleaning", "Move-In/Move-Out", "Airbnb Turnover", "Recurring Service", "Other"];
const FREQUENCIES = ["One-Time", "Weekly", "Bi-Weekly", "Monthly", "Not Sure Yet"];

export default function Contact() {
  useEffect(() => { document.title = "Contact Us | Eco Ready Solutions, LLC | Royal Oak, MI"; }, []);

  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    email: "",
    property_type: "",
    square_footage: "",
    services: [],
    frequency: "",
    preferred_start_date: undefined,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fill out the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_start_date: form.preferred_start_date ? format(form.preferred_start_date, "yyyy-MM-dd") : null,
      };
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Thank you! Your request has been received. We'll follow up promptly.");
      setForm({
        full_name: "", company_name: "", phone: "", email: "",
        property_type: "", square_footage: "", services: [], frequency: "",
        preferred_start_date: undefined, notes: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        eyebrow="Contact"
        title="Let's Be Clean Together."
        subtitle="Get in touch and let's build a cleaning plan that works for your property."
        image={HERO_IMG}
      />

      <section className="eco-section bg-white">
        <div className="eco-container grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14">
          {/* FORM */}
          <div className="eco-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="p-7 md:p-9 border-b border-[#D6DDD9] bg-[#F4F6F5]">
              <span className="eco-eyebrow">Free Estimate</span>
              <h2 className="text-2xl md:text-3xl font-bold mt-2">Request a Free Estimate</h2>
              <p className="mt-3 text-[15px] text-[#555555] leading-[1.7]">
                To help us prepare an accurate estimate, please share a few details about your property. We will follow
                up promptly with a clear, professional proposal.
              </p>
            </div>

            <form data-testid="estimate-form" onSubmit={onSubmit} className="p-7 md:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required error={errors.full_name}>
                  <Input data-testid="form-fullname" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jane Doe" />
                </Field>
                <Field label="Company Name (Optional)">
                  <Input data-testid="form-company" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="Your business" />
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <Input data-testid="form-phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(248) 555-0100" />
                </Field>
                <Field label="Email Address" required error={errors.email}>
                  <Input data-testid="form-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
                </Field>
                <Field label="Property Type">
                  <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
                    <SelectTrigger data-testid="form-property-type"><SelectValue placeholder="Select a type" /></SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Approximate Square Footage">
                  <Input data-testid="form-sqft" value={form.square_footage} onChange={(e) => update("square_footage", e.target.value)} placeholder="e.g. 1,800 sq ft" />
                </Field>
              </div>

              <Field label="Services Needed">
                <div className="grid sm:grid-cols-2 gap-2.5 mt-1">
                  {SERVICES.map((s) => (
                    <label key={s} className="flex items-start gap-2.5 cursor-pointer select-none rounded-[8px] border border-[#D6DDD9] px-3.5 py-3 hover:border-[#2D6A4F] transition-colors">
                      <Checkbox
                        data-testid={`form-service-${s.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                        checked={form.services.includes(s)}
                        onCheckedChange={() => toggleService(s)}
                        className="mt-0.5"
                      />
                      <span className="text-[14.5px] text-[#1A1A1A]">{s}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Preferred Frequency">
                  <Select value={form.frequency} onValueChange={(v) => update("frequency", v)}>
                    <SelectTrigger data-testid="form-frequency"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred Start Date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="form-start-date"
                        className="flex items-center justify-between w-full h-10 px-3 rounded-[8px] border border-[#D6DDD9] bg-white text-[14.5px] text-left hover:border-[#2D6A4F] transition-colors"
                      >
                        <span className={form.preferred_start_date ? "text-[#1A1A1A]" : "text-[#888]"}>
                          {form.preferred_start_date ? format(form.preferred_start_date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarIcon size={16} className="text-[#2D6A4F]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={form.preferred_start_date} onSelect={(d) => update("preferred_start_date", d)} initialFocus />
                    </PopoverContent>
                  </Popover>
                </Field>
              </div>

              <Field label="Additional Notes">
                <Textarea
                  data-testid="form-notes"
                  rows={5}
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Anything else we should know about your property or schedule?"
                />
              </Field>

              <button
                type="submit"
                data-testid="form-submit"
                disabled={submitting}
                className="eco-btn eco-btn-primary w-full sm:w-auto"
              >
                {submitting ? "Sending..." : (<>Send My Request <Send size={16} /></>)}
              </button>
            </form>
          </div>

          {/* CONTACT DETAILS */}
          <aside className="space-y-6">
            <div>
              <span className="eco-eyebrow">Reach Us</span>
              <h2 className="eco-h2 mt-3">Get in Touch</h2>
              <ul className="mt-7 space-y-4 text-[15.5px]">
                <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-[#2D6A4F]" /><span>{SITE.address}</span></li>
                <li className="flex items-center gap-3"><Phone size={18} className="text-[#2D6A4F]" /><a href={SITE.phoneHref} className="hover:text-[#2D6A4F]">{SITE.phone}</a></li>
                <li className="flex items-center gap-3"><Mail size={18} className="text-[#2D6A4F]" /><a href={SITE.emailHref} className="hover:text-[#2D6A4F]">{SITE.email}</a></li>
                <li className="flex items-center gap-3"><Globe size={18} className="text-[#2D6A4F]" /><a href={SITE.websiteHref} className="hover:text-[#2D6A4F]">{SITE.website}</a></li>
                <li className="flex items-start gap-3"><Clock size={18} className="mt-0.5 text-[#2D6A4F]" /><span>{SITE.hours}</span></li>
              </ul>
            </div>

            <p className="text-[15px] text-[#555555] leading-[1.75]">
              We respond to all inquiries promptly and are happy to schedule a walkthrough or phone consultation at
              your convenience. Whether you are a business owner, property manager, or homeowner — we look forward to
              hearing from you.
            </p>

            <blockquote className="eco-quote">
              &ldquo;We are committed to earning your business — one clean at a time.&rdquo;
            </blockquote>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-[13px] font-semibold text-[#1A1A1A]">
        {label} {required && <span className="text-[#2D6A4F]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-[12.5px] text-red-600">{error}</p>}
    </div>
  );
}
